import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";

const formatTime = (seconds) => {
  const safe = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
};

const StateMatchedBreathingGuide = ({ protocol, onFinish }) => {
  const inhaleSeconds = Math.max(2, Number(protocol?.inhaleSeconds) || 4);
  const exhaleSeconds = Math.max(2, Number(protocol?.exhaleSeconds) || 5);
  const totalSeconds = Math.max(60, Number(protocol?.durationMinutes || 3) * 60);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState("inhale");
  const [remaining, setRemaining] = useState(totalSeconds);
  const sessionDeadlineRef = useRef(null);
  const phaseDeadlineRef = useRef(null);
  const remainingRef = useRef(totalSeconds);
  const onFinishRef = useRef(onFinish);

  useEffect(() => {
    onFinishRef.current = onFinish;
  }, [onFinish]);

  const phaseSeconds = phase === "inhale" ? inhaleSeconds : exhaleSeconds;
  const prompt = phase === "inhale" ? "Breathe in" : "Breathe out";

  const reset = useCallback(() => {
    setRunning(false);
    setPhase("inhale");
    setRemaining(totalSeconds);
    remainingRef.current = totalSeconds;
    sessionDeadlineRef.current = null;
    phaseDeadlineRef.current = null;
  }, [totalSeconds]);

  useEffect(() => {
    reset();
  }, [inhaleSeconds, exhaleSeconds, reset]);

  useEffect(() => {
    if (!running) return undefined;

    const now = Date.now();
    sessionDeadlineRef.current = now + remainingRef.current * 1000;
    phaseDeadlineRef.current = now + phaseSeconds * 1000;

    const timer = window.setInterval(() => {
      const current = Date.now();
      const left = Math.max(0, (sessionDeadlineRef.current - current) / 1000);
      remainingRef.current = left;
      setRemaining(left);

      if (left <= 0) {
        window.clearInterval(timer);
        setRunning(false);
        setRemaining(0);
        remainingRef.current = 0;
        sessionDeadlineRef.current = null;
        phaseDeadlineRef.current = null;
        onFinishRef.current?.();
        return;
      }

      if (current >= phaseDeadlineRef.current) {
        setPhase((currentPhase) => {
          const next = currentPhase === "inhale" ? "exhale" : "inhale";
          const nextSeconds = next === "inhale" ? inhaleSeconds : exhaleSeconds;
          phaseDeadlineRef.current = current + nextSeconds * 1000;
          return next;
        });
      }
    }, 100);

    return () => window.clearInterval(timer);
  }, [running, phaseSeconds, inhaleSeconds, exhaleSeconds]);

  const circleStyle = useMemo(
    () => ({
      width: "min(62vw, 230px)",
      height: "min(62vw, 230px)",
      transform: phase === "inhale" ? "scale(1)" : "scale(0.62)",
      transitionProperty: "transform",
      transitionDuration: `${phaseSeconds}s`,
      transitionTimingFunction: "ease-in-out",
      background:
        "radial-gradient(circle at 35% 30%, rgba(255,255,255,0.9), rgba(168,226,218,0.88) 38%, rgba(35,160,145,0.88) 100%)",
      boxShadow: "0 18px 50px rgba(20, 118, 108, 0.16)",
    }),
    [phase, phaseSeconds],
  );

  const toggle = () => {
    if (remaining <= 0) {
      reset();
      return;
    }
    if (running) {
      const now = Date.now();
      const left = sessionDeadlineRef.current
        ? Math.max(0, (sessionDeadlineRef.current - now) / 1000)
        : remainingRef.current;
      remainingRef.current = left;
      setRemaining(left);
      sessionDeadlineRef.current = null;
      phaseDeadlineRef.current = null;
      setRunning(false);
      return;
    }
    setRunning(true);
  };

  return (
    <div
      className="mt-5 overflow-hidden rounded-[28px] border p-5 text-center sm:p-7"
      style={{
        borderColor: "var(--forr-line-soft)",
        backgroundColor: "var(--forr-surface-tint)",
      }}
      data-testid="state-matched-breathing-guide"
    >
      <div className="forr-overline">Follow along</div>
      <h3 className="mt-2 font-display text-2xl forr-text-ink">{protocol?.title || "State-matched breathing"}</h3>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed forr-text-soft">
        The prompt and circle run continuously. Keep the breath comfortable; there are no breath holds.
      </p>

      <div className="mt-7 flex min-h-[270px] items-center justify-center">
        <div className="flex items-center justify-center rounded-full" style={circleStyle} aria-hidden="true">
          <div className="rounded-full bg-white/80 px-5 py-3 font-display text-xl forr-text-teal-deep">
            {running ? prompt : remaining <= 0 ? "Complete" : "Ready"}
          </div>
        </div>
      </div>

      <div className="mt-3 text-3xl font-display forr-text-ink" aria-live="polite">
        {running ? prompt : remaining <= 0 ? "Session complete" : "Start when ready"}
      </div>
      <div className="mt-2 text-sm forr-text-mute">
        {inhaleSeconds}s in · {exhaleSeconds}s out · {formatTime(remaining)} remaining
      </div>

      {protocol?.rationale && (
        <p className="mx-auto mt-4 max-w-xl text-xs leading-relaxed forr-text-mute">
          {protocol.rationale}
        </p>
      )}

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium forr-btn-primary"
        >
          {running ? <Pause size={16} /> : <Play size={16} />}
          {running ? "Pause" : remaining <= 0 ? "Start again" : "Start breathing"}
        </button>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm forr-btn-secondary"
        >
          <RotateCcw size={16} /> Reset
        </button>
      </div>
    </div>
  );
};

export default StateMatchedBreathingGuide;
